import React, { useState, useEffect } from 'react';
import { Camera, Lock, User, Mail, X, Check, ChevronDown } from 'lucide-react';
const ProfileImageHeader = ({ image, username, onImagePick }) => (
  <div style={styles.headerImageContainer}>
    <div style={styles.headerImageWrapper}>
      <img 
        src={image || '/api/placeholder/80/80'} 
        alt="Profile"
        style={styles.headerImage}
      />
      <div 
        style={styles.headerImageOverlay}
        onClick={onImagePick}
      >
        <div style={styles.editIconWrapper}>
          <Camera size={20} color="#fff" />
        </div>
      </div>
      <div style={styles.editIconBadge}>
        <Camera size={14} color="#fff" />
      </div>
    </div>
    <div style={styles.headerUsername}>{username}</div>
  </div>
);

const ProfileEditModal = ({
  isOpen,
  onClose,
  profileImage,
  username,
  email,
  onSave,
  onImagePick
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPasswordExpanded, setIsPasswordExpanded] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      resetForm();
    }, 300);
  };

  const resetForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setIsPasswordExpanded(false);
  };

  const handleSave = async () => {
    // If password section is expanded, validate password changes
    if (isPasswordExpanded) {
      // Check if all password fields are filled
      if (!oldPassword || !newPassword || !confirmPassword) {
        setError('Please fill in all password fields');
        return;
      }

      // Validate new password
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      if (newPassword.length < 6) {
        setError('New password must be at least 8 characters long');
        return;
      }

      // Prepare save data
      const saveData = {
        oldPassword,
        newPassword,
        confirmPassword
      };

      // Set success state
      setShowSuccess(true);
      
      try {
        // Save changes
        await onSave(saveData);

        // Close modal after successful save
        setTimeout(() => {
          handleClose();
          setShowSuccess(false);
        }, 1000);
      } catch (saveError) {
        // Handle save error
        setError('Error saving changes');
        setShowSuccess(false);
      }
    } else {
      // If password section is not expanded, just close the modal
      handleClose();
    }
  };

  const togglePasswordSection = () => {
    setIsPasswordExpanded(!isPasswordExpanded);
    if (!isPasswordExpanded) {
      setTimeout(() => {
        document.getElementById('oldPassword')?.focus();
      }, 300);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      ...styles.backdrop,
      opacity: isAnimating ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        ...styles.modal,
        transform: isAnimating ? 'scale(1)' : 'scale(0.95)',
        opacity: isAnimating ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <ProfileImageHeader 
          image={profileImage}
          username={username}
          onImagePick={onImagePick}
        />
        
        <div style={{
          ...styles.header,
          marginTop: '48px',
        }}>
          <h2 style={styles.title}>Edit Profile</h2>
          <button
            style={styles.closeButton}
            onClick={handleClose}
            onMouseEnter={e => e.target.style.transform = 'rotate(90deg)'}
            onMouseLeave={e => e.target.style.transform = 'rotate(0deg)'}
          >
            <X size={24} />
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.form}>
            <div style={styles.inputGroup}>
              <div style={styles.inputIcon}><User size={18} /></div>
              <input
                type="text"
                value={username}
                style={styles.input}
                readOnly
                placeholder="Username"
              />
            </div>

            <div style={styles.inputGroup}>
              <div style={styles.inputIcon}><Mail size={18} /></div>
              <input
                type="email"
                value={email}
                style={styles.input}
                readOnly
                placeholder="Email"
              />
            </div>

            <button 
              style={styles.passwordToggle}
              onClick={togglePasswordSection}
            >
              <Lock size={18} />
              <span>Change Password</span>
              <ChevronDown 
                size={18}
                style={{
                  transform: isPasswordExpanded ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </button>

            <div style={{
              ...styles.passwordSection,
              maxHeight: isPasswordExpanded ? '300px' : '0',
              opacity: isPasswordExpanded ? 1 : 0,
              margin: isPasswordExpanded ? '16px 0' : '0',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <div style={styles.inputGroup}>
                <input
                  id="oldPassword"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Current Password"
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={styles.input}
                  placeholder="New Password"
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Confirm New Password"
                />
              </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button 
                style={styles.cancelButton} 
                onClick={handleClose}
                onMouseEnter={e => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={e => e.target.style.backgroundColor = '#fff'}
              >
                Cancel
              </button>
              <button 
                style={{
                  ...styles.saveButton,
                  backgroundColor: showSuccess ? '#10b981' : '#3D3935'
                }} 
                onClick={handleSave}
                disabled={showSuccess}
              >
                {showSuccess ? <Check size={20} /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const styles = {
  headerImageContainer: {
    position: 'absolute',
    top: '-48px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  headerUsername: {
    color: '#111827',
    fontSize: '20px',
    fontWeight: '600',
    marginTop: '4px',
    marginBottom:"20px"
  },
  editIconBadge: {
    position: 'absolute',
    bottom: '10px',
    right: '2px',
    width: '24px',
    height: '24px',
    backgroundColor: '#3D3935',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',

  },
  editIconWrapper: {
    backgroundColor: '#3D3935',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerImageWrapper: {
    position: 'relative',
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '4px solid #fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    ':hover': {
      transform: 'scale(1.05)',
    }
  },
  headerImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  headerImageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    cursor: 'pointer',
    ':hover': {
      opacity: 1,
    }
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'visible',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    position: 'relative',
  },
  header: {
    padding: '40px 24px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    marginTop: 10,
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    alignItems:"center",
    marginBottom:"-70px"
    
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',

  },
  content: {
    padding: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    color: '#6b7280',
  },
  input: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: '#f9fafb',
  },
  passwordToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#374151',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  passwordSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'hidden',
  },
  error: {
    color: '#ef4444',
    fontSize: '14px',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  saveButton: {
    padding: '8px 24px',
    borderRadius: '8px',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  },
};

export default ProfileEditModal ;